import React, { useState, useContext } from "react"
import { FirebaseContext } from "./Firebase"

const DeleteUser = ({ signout }) => {
  const [loading, setLoading] = useState(false)
  const [deletionSuccess, setDeletionSuccess] = useState(false)
  const [deletionError, setDeletionError] = useState(null)
  const [show, setShow] = useState(false)
  const [deleteInput, setDeleteInput] = useState("")
  const [inputError, setInputError] = useState(null)

  const fb = useContext(FirebaseContext)
  const { user, app: firebase } = fb

  const onClose = () => {
    setShow(false)
    setDeletionSuccess(false)
    setDeletionError(null)
    setDeleteInput("")
    setInputError(null)
  }

  const updateDeletionStatus = async status => {
    const deletionRequestsRef = firebase
      .firestore()
      .collection("deletionRequests")
    try {
      await deletionRequestsRef.doc(user.uid).set({
        email: user.email,
        status,
        timestamp: firebase.firestore.FieldValue.serverTimestamp(),
      })
      console.log(`Status updated to ${status}`)
    } catch (error) {
      console.error("Error updating status", error)
    }
  }

  const deleteUserAndData = async (e) => {
    e.preventDefault()

    // Validate input
    if (deleteInput.toUpperCase() !== "DELETE") {
      setInputError("Please type DELETE in the field")
      return
    }

    setLoading(true)
    setDeletionError(null)
    setInputError(null)

    try {
      await updateDeletionStatus("deletionStarted")

      const deleteUserAndDataFn = firebase
        .functions()
        .httpsCallable("deleteUserAndData")
      const result = await deleteUserAndDataFn()

      if (result.data.success) {
        console.log("User and data deleted")
        await updateDeletionStatus("deletionFinished")
        setDeletionSuccess(true)
        setLoading(false)
      } else {
        console.log("Failed to delete user and data")
        throw new Error("Failed to delete user and data")
      }
    } catch (error) {
      console.log("user delete process error", error)
      await updateDeletionStatus("deletionError")
      setDeletionError(error.message || "An error occurred during deletion")
      setLoading(false)
    }
  }

  if (!show) {
    return (
      <button
        onClick={() => setShow(true)}
        className="text-sm text-gray-500 hover:text-red-600 underline transition"
      >
        Delete Account
      </button>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-md w-full mx-4 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold"
        >
          &times;
        </button>

        {deletionSuccess ? (
          <div className="flex flex-col items-center justify-center p-8">
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Account Deleted</h2>
              <p className="text-gray-600">
                Your account and associated data have been permanently removed.
              </p>
              <button
                onClick={() => {
                  signout()
                  window.location.href = "/"
                }}
                className="w-full px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition"
              >
                Return to Homepage
              </button>
            </div>
          </div>
        ) : deletionError ? (
          <div className="flex flex-col items-center justify-center p-8">
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-800">Deletion Failed</h2>
              <p className="text-gray-600">{deletionError}</p>
              <button
                onClick={() => {
                  setDeletionError(null)
                  setLoading(false)
                }}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : loading ? (
          <div className="flex flex-col items-center justify-center p-16">
            <div className="lds-dual-ring"></div>
            <p className="mt-4 text-gray-600">Deleting your account...</p>
          </div>
        ) : (
          <form onSubmit={deleteUserAndData} className="p-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">
              Delete your account
            </h2>
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                This action is <strong>permanent</strong> and cannot be undone. All of your data will be removed, including:
              </p>
              <ul className="text-gray-600 text-sm list-disc list-inside space-y-1">
                <li>Your profile information</li>
                <li>All your racing lines</li>
                <li>Your favorites and preferences</li>
              </ul>
              <div>
                <label
                  htmlFor="delete"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Please type <strong>DELETE</strong> below to confirm:
                </label>
                <input
                  type="text"
                  name="delete"
                  id="delete"
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                  placeholder="Type DELETE here"
                />
                {inputError && (
                  <p className="text-red-600 text-sm mt-1">{inputError}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition font-semibold"
              >
                Delete Account
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default DeleteUser
